import { createObjectPickerOption } from '@components/ui/ObjectPicker'

import { getExpandedPath } from '@helpers'
import { EXPAND_FIELD_PREFIX, FIELD_VALUE_TYPE, OBJECT_FIELD_TYPE } from '@constants'
import { FormRow, ObjectDataRecord } from '@types'

const getValueByPath = (source: unknown, path: string[]) => {
  if (!source || typeof source !== 'object' || !path.length) {
    return undefined
  }

  let currentValue: unknown = source

  for (const pathSegment of path) {
    if (Array.isArray(currentValue)) {
      currentValue = currentValue
        .map(item => {
          if (!item || typeof item !== 'object') {
            return undefined
          }

          return (item as Record<string, unknown>)[pathSegment]
        })
        .filter(item => item !== undefined && item !== null && item !== '')

      continue
    }

    if (!currentValue || typeof currentValue !== 'object') {
      return undefined
    }

    currentValue = (currentValue as Record<string, unknown>)[pathSegment]
  }

  if (Array.isArray(currentValue)) {
    return currentValue
      .flatMap(item => (Array.isArray(item) ? item : [item]))
      .filter(item => item !== undefined && item !== null && item !== '')
      .join(', ')
  }

  return currentValue
}

const getObjectLinkDisplayPath = (field: FormRow) => {
  if (field.pathArray?.length) {
    let pathArray = field.pathArray

    if (field.embeddedObjectCode !== field.dropDownObjectCode) {
      const objectIndex = pathArray.findIndex(item => item.objectCode === field.embeddedObjectCode)

      if (objectIndex !== -1) {
        pathArray = pathArray.slice(objectIndex)
      }
    }

    const path = pathArray.map(pathItem => pathItem.field).join('.')

    return getExpandedPath(path)
  }

  if (field.objectValue) {
    return [field.objectValue]
  }

  return []
}

const isIdField = (fieldName: string) => {
  return (
    fieldName === '_id' ||
    fieldName === 'id' ||
    fieldName === 'internal_id' ||
    fieldName.endsWith('_id')
  )
}

const hasMatchingIdValue = (item: Record<string, unknown>, value: string | number) => {
  return Object.entries(item).some(([fieldName, fieldValue]) => {
    return isIdField(fieldName) && fieldValue === value
  })
}

const findExpandedArrayItem = (
  expandedValue: unknown,
  value: string | number
): Record<string, unknown> | undefined => {
  if (!Array.isArray(expandedValue)) {
    return undefined
  }

  return expandedValue.find(item => {
    if (!item || typeof item !== 'object') {
      return false
    }

    return hasMatchingIdValue(item as Record<string, unknown>, value)
  }) as Record<string, unknown> | undefined
}

const getExpandedObjectLabel = (expandedValue: unknown, field: FormRow) => {
  if (!expandedValue || typeof expandedValue !== 'object') {
    return undefined
  }

  const displayPath = getObjectLinkDisplayPath(field)

  return getValueByPath(expandedValue, displayPath)
}

const getObjectLinkValue = (
  value: any,
  valueName: string,
  field: FormRow,
  objectValue: string,
  row: ObjectDataRecord,
  isArray: boolean
) => {
  const coreValueName = field.value || field.code
  const expandedValue = row[`${EXPAND_FIELD_PREFIX}${coreValueName}`]

  if (value === null || value === undefined || value === '') {
    return null
  }

  if (isArray) {
    const expandedItem = findExpandedArrayItem(expandedValue, value as string | number)
    const labelFromExpanded = getExpandedObjectLabel(expandedItem, field)

    return createObjectPickerOption(
      expandedValue,
      undefined,
      objectValue,
      undefined,
      value,
      (labelFromExpanded as string) ?? ''
    )
  }

  if (expandedValue && typeof expandedValue === 'object' && !Array.isArray(expandedValue)) {
    const labelFromExpanded = getExpandedObjectLabel(expandedValue, field)

    if (labelFromExpanded !== undefined && labelFromExpanded !== null) {
      return createObjectPickerOption(expandedValue, undefined, objectValue, undefined, value)
    }
  }

  return { id: value, label: row[`${valueName}.${objectValue}`] ?? '' }
}

// Формирует значение по-умолчанию в зависимости от типа филда
export const getDefaultValueByType = (field: FormRow, row: ObjectDataRecord) => {
  const isArray = field.type === OBJECT_FIELD_TYPE.ARRAY

  const valueType = field.valueType
  const valueName = field.code
  const objectValue = field.objectValue || ''
  // Не array оборачиваем в массив для унификации обработки (см. ниже)
  const originalValue = isArray ? ((row[valueName] || []) as any[]) : [row[valueName]]

  const resultValue = originalValue.map(value => {
    switch (valueType) {
      case FIELD_VALUE_TYPE.DATETIME:
      case FIELD_VALUE_TYPE.BINARY:
        return value ?? null
      case FIELD_VALUE_TYPE.BOOLEAN:
        return value ?? false
      case FIELD_VALUE_TYPE.OBJ_PK_LINK:
      case FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK:
        return getObjectLinkValue(value, valueName, field, objectValue, row, isArray)

      default:
        return value ?? ''
    }
  })

  return [valueName, isArray ? resultValue : resultValue[0]]
}
